import type { CandidateStatusParam } from '@/hooks/useRouterQuery';

/**
 * Single source of truth for translating raw `cadence_template_candidate.status`
 * values into human-readable labels. Used by:
 *   - the staging-table status pill (`STATUS_LABELS` in `CampaignTemplateStaging.tsx`)
 *   - the candidate-status filter dropdown options (`Filters2/Filters.tsx`)
 *   - any future status badge / surface
 *
 * Falls back to the raw string when an unknown status sneaks in (defensive —
 * means we don't ship a broken UI when the worker adds a new status before
 * the client redeploys).
 */
const LABELS: Record<CandidateStatusParam, string> = {
  pending: 'Pending',
  researching: 'Researching',
  research_failed: 'Failed',
  gathering_sources: 'Searching the web',
  synthesizing: 'Synthesizing dossier',
  synthesize_failed: 'Dossier failed',
  filter_failed: 'Filtered out',
  personalizing: 'Drafting',
  personalize_failed: 'Drafting failed',
  rate_limited: 'Rate limited',
  account_disconnected: 'LinkedIn disconnected',
  awaiting_capacity: 'Waiting for capacity',
  retrying: 'Retrying',
  pending_review: 'Needs review',
  rejected: 'Rejected',
  promoted: 'Enrolled',
  removed: 'Removed',
};

export function humanizeCandidateStatus(status: string | null | undefined): string {
  if (status == null || status === '') return '';
  // Cast is safe: unknown values fall through to the raw string below.
  const known = LABELS[status as CandidateStatusParam];
  return known ?? status;
}

/** Convenience for code paths that already know the status is well-typed. */
export const candidateStatusLabel = (status: CandidateStatusParam): string => LABELS[status];

/**
 * Human label for the reason a Campaign Switch candidate was errored out. A
 * switch-errored candidate is `status = 'filter_failed'` disambiguated by
 * `failure_type`:
 *   - `switch_no_match` → the contact's attribute matched no configured range
 *   - `switch_no_data`  → the attribute couldn't be resolved for the contact
 * Falls back to the generic filtered-out label for any other failure_type.
 */
export function humanizeSwitchOutcome(failureType: string | null | undefined): string {
  if (failureType === 'switch_no_match') return 'No matching range';
  if (failureType === 'switch_no_data') return 'No age data';
  return 'Filtered out';
}
